import React from 'react';
import { useTranslation } from 'react-i18next';
import { Car, MapPin, Gauge, Calendar, User, Building2 } from 'lucide-react';
import { formatServiceDateRange } from '@/utils/expenseDisplay';

/**
 * Service context for a pending expense report (plate, driver, route, km, dates).
 */
export default function ExpenseReportServiceInfo({ report }) {
  const { t, i18n } = useTranslation();
  const dateLocale = (i18n.language || '').startsWith('en') ? 'en-US' : 'es-ES';
  const svc = report?.service;
  const orderId = svc?.order_id || report?.order_id;

  if (!orderId && !svc?.plate) return null;

  const vehicleLine = [svc?.brand, svc?.model].filter(Boolean).join(' ');
  const pickupDates = formatServiceDateRange(svc?.pickup_date_from, svc?.pickup_date_to, dateLocale);
  const deliveryDates = formatServiceDateRange(
    svc?.delivery_date_from,
    svc?.delivery_date_to,
    dateLocale,
  );

  return (
    <div className="mb-3 p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Car className="w-4 h-4 text-blue-700 shrink-0" />
        <span className="font-bold text-blue-900 text-base">{svc?.plate || t('gps_live_na')}</span>
        {vehicleLine && <span className="text-blue-800">{vehicleLine}</span>}
        {orderId && (
          <span className="text-xs text-blue-600 font-mono">
            {t('admin_expense_service_id', { id: String(orderId).slice(0, 8) })}
          </span>
        )}
      </div>

      {report?.driver_name && (
        <div className="flex items-center gap-2 text-gray-700">
          <User className="w-3.5 h-3.5 shrink-0" />
          <span>{report.driver_name}</span>
        </div>
      )}

      {svc?.client_name && (
        <div className="flex items-center gap-2 text-gray-700">
          <Building2 className="w-3.5 h-3.5 shrink-0" />
          <span>{svc.client_name}</span>
        </div>
      )}

      {(svc?.pickup_address || svc?.delivery_address) && (
        <div className="flex items-start gap-2 text-gray-700">
          <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <div className="min-w-0">
            {svc.pickup_address && (
              <div>
                <span className="text-xs text-gray-500">{t('pickup')}: </span>
                {svc.pickup_address}
              </div>
            )}
            {svc.delivery_address && (
              <div>
                <span className="text-xs text-gray-500">{t('delivery')}: </span>
                {svc.delivery_address}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-gray-700">
        {svc?.distance_km != null && svc.distance_km !== '' && (
          <span className="flex items-center gap-1">
            <Gauge className="w-3.5 h-3.5" />
            {t('admin_expense_service_km', { km: Number(svc.distance_km).toLocaleString(dateLocale) })}
          </span>
        )}
        {pickupDates && (
          <span className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {t('admin_expense_service_pickup_dates', { dates: pickupDates })}
          </span>
        )}
        {deliveryDates && (
          <span className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {t('admin_expense_service_delivery_dates', { dates: deliveryDates })}
          </span>
        )}
      </div>
    </div>
  );
}
